const DEVICE_DATA_COLLECTION_FRAME_ID = 'cybersource-device-data-collection';
const DEVICE_DATA_COLLECTION_TIMEOUT_MS = 10000;

const createOtpSetupError = (response) => {
    const error = new Error(response?.error_message || 'Unable to prepare card authentication');
    error.errorCode = response?.error_code;
    return error;
};

const requestOtpSetup = (setupOtp, requestDto, authToken) => {
    return new Promise((resolve, reject) => {
        if (!requestDto) {
            reject(new Error('Payment details are required for card authentication setup'));
            return;
        }

        let isSettled = false;

        const resolveOnce = (response) => {
            if (isSettled) return;
            isSettled = true;
            resolve(response);
        };

        const rejectOnce = (error) => {
            if (isSettled) return;
            isSettled = true;
            reject(error);
        };

        try {
            const request = setupOtp(requestDto, authToken, resolveOnce, rejectOnce);
            request?.catch?.(rejectOnce);
        } catch (error) {
            rejectOnce(error);
        }
    });
};

export const renderDeviceDataCollectionHtml = (
    ddcHtml,
    documentRef = document,
    timeoutMs = DEVICE_DATA_COLLECTION_TIMEOUT_MS) => {
    if (!ddcHtml?.trim()) {
        return Promise.reject(new Error('Card authentication setup returned empty DDC HTML'));
    }

    documentRef.getElementById(DEVICE_DATA_COLLECTION_FRAME_ID)?.remove();

    const iframe = documentRef.createElement('iframe');
    iframe.id = DEVICE_DATA_COLLECTION_FRAME_ID;
    iframe.title = 'Card authentication device data collection';
    iframe.hidden = true;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.srcdoc = ddcHtml;

    return new Promise((resolve) => {
        let isComplete = false;
        let timeoutId;
        const timerHost = documentRef.defaultView || window;

        const complete = () => {
            if (isComplete) return;
            isComplete = true;
            timerHost.clearTimeout(timeoutId);
            resolve();
        };

        iframe.addEventListener('load', complete, { once: true });
        // Device data collection is best effort. Continue the authentication
        // flow if the iframe does not emit a load event within the 3DS timeout.
        timeoutId = timerHost.setTimeout(complete, timeoutMs);
        documentRef.body.appendChild(iframe);
    });
};

export const prepareOtpAuthentication = async ({
    setupOtp,
    requestDto,
    authToken,
    renderDdcHtml = renderDeviceDataCollectionHtml,
}) => {
    const response = await requestOtpSetup(setupOtp, requestDto, authToken);

    // Mastercard transactions do not require CyberSource device data
    // collection, so they can continue directly to the existing OTP flow.
    if (response?.should_call_ddc === false) {
        return;
    }

    if (!response?.succeeded) {
        throw createOtpSetupError(response);
    }

    await renderDdcHtml(response.ddc_html);
};
